import React, { useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Grid,
  Text,
  Spinner,
  Select,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import {
  Buildings,
  CheckCircle,
  WarningCircle,
  CalendarBlank,
  MagnifyingGlass,
} from 'phosphor-react';
import { useAuditoriaInteligente } from '../../../../hooks/useAuditoriaInteligente';
import { num, pct } from '../format';
import { IFechamentoEmpresa, IFechamentoDepto, AuditoriaBadge } from '../types';
import { KpiCard } from '../components/KpiCard';
import { InfoBloco, FILTRO } from '../components/InfoPopover';
import { EmptyState } from '../components/EmptyState';
import { useAuth } from '../../../../providers/auth';
import { UI } from '../ui';

interface FechamentoScreenProps {
  onOpenEmpresa: (id: number) => void;
  onOpenEmpresaScreen: (id: number, screen: string) => void;
}

const STATUS_COLOR: Record<AuditoriaBadge, string> = {
  green: UI.pos,
  amber: UI.warn,
  red: UI.neg,
};

const STATUS_BADGE: Record<AuditoriaBadge, { bg: string; color: string; label: string }> = {
  green: { bg: UI.posSoft, color: UI.pos, label: 'No prazo' },
  amber: { bg: UI.warnSoft, color: UI.warn, label: 'Atenção' },
  red: { bg: UI.negSoft, color: UI.neg, label: 'Atrasada' },
};

// Depto -> submódulo de causa (drill do gargalo).
const DEPTO_SCREEN: Record<string, string> = {
  contabil: 'contabil',
  fiscal: 'integracao',
  folha: 'pessoal',
};
const DEPTO_LABEL: Record<string, string> = {
  contabil: 'Contábil',
  fiscal: 'Fiscal',
  folha: 'Folha',
};

/** Cor por nível de atraso médio (dias). */
const corAtraso = (dias: number): string => {
  if (dias > 30) return UI.neg;
  if (dias > 10) return UI.warn;
  return UI.pos;
};

function FechamentoBadge({ status }: { status: AuditoriaBadge }) {
  const s = STATUS_BADGE[status];
  return (
    <Badge
      style={{ backgroundColor: s.bg, color: s.color }}
      borderRadius="full"
      px={2.5}
      py={0.5}
      fontSize="11.5px"
      fontWeight={700}
      textTransform="none"
    >
      {s.label}
    </Badge>
  );
}

const FILTROS: { value: string; label: string }[] = [
  { value: '', label: 'Todos os status' },
  { value: 'green', label: 'No prazo' },
  { value: 'amber', label: 'Atenção' },
  { value: 'red', label: 'Atrasadas' },
];

function DeptoCell({ depto, onOpen }: { depto: IFechamentoDepto | null; onOpen?: () => void }) {
  if (!depto) {
    return <Text color={UI.muted}>—</Text>;
  }
  return (
    <Flex
      align="center"
      gap={2}
      cursor={onOpen ? 'pointer' : undefined}
      onClick={
        onOpen
          ? ev => {
              ev.stopPropagation();
              onOpen();
            }
          : undefined
      }
      _hover={onOpen ? { textDecoration: 'underline' } : undefined}
      title={depto.responsavel ? `Responsável: ${depto.responsavel}` : undefined}
    >
      <Box w="8px" h="8px" borderRadius="50%" bg={STATUS_COLOR[depto.status]} flexShrink={0} />
      <Box>
        <Text lineHeight="1.2">{depto.ate ?? '—'}</Text>
        {depto.responsavel && (
          <Text fontSize="10.5px" color={UI.muted} noOfLines={1}>
            {depto.responsavel}
          </Text>
        )}
      </Box>
    </Flex>
  );
}

const Glass = ({
  title,
  sub,
  children,
}: {
  title: string;
  sub?: string;
  children: React.ReactNode;
}) => (
  <Box
    borderRadius="18px"
    p={5}
    bg="rgba(255,255,255,0.7)"
    backdropFilter="blur(14px) saturate(160%)"
    border="1px solid rgba(255,255,255,0.85)"
    boxShadow="0 12px 34px -18px rgba(15,23,42,0.35)"
  >
    <Flex justify="space-between" align="baseline" mb={4} gap={2}>
      <Text fontSize="13px" fontWeight={800} color={UI.ink}>
        {title}
      </Text>
      {sub && (
        <Text fontSize="11px" color={UI.faint} fontWeight={600}>
          {sub}
        </Text>
      )}
    </Flex>
    {children}
  </Box>
);

const FAIXAS = [
  { label: 'No prazo', min: -Infinity, max: 1, color: UI.pos },
  { label: '1–30 dias', min: 1, max: 31, color: '#1AA6B7' },
  { label: '31–60 dias', min: 31, max: 61, color: UI.warn },
  { label: '+60 dias', min: 61, max: Infinity, color: UI.neg },
];

export function FechamentoScreen({ onOpenEmpresa, onOpenEmpresaScreen }: FechamentoScreenProps) {
  const { getFechamento } = useAuditoriaInteligente();
  const { temVisaoTodosGrupos } = useAuth();
  const visaoCarteira = temVisaoTodosGrupos();
  const [filtro, setFiltro] = useState('');
  const [busca, setBusca] = useState('');

  const { data, isFetching, isError } = useQuery<IFechamentoEmpresa[]>({
    queryKey: ['ai-escritorio-fechamento'],
    queryFn: getFechamento,
    enabled: visaoCarteira,
  });

  const empresas = useMemo(() => data ?? [], [data]);

  const resumo = useMemo(() => {
    const noPrazo = empresas.filter(e => e.status === 'green').length;
    const atrasadas = empresas.filter(e => e.status === 'red').length;
    const atrasoMedio = empresas.length
      ? Math.round(empresas.reduce((s, e) => s + num(e.atraso), 0) / empresas.length)
      : 0;
    return { noPrazo, atrasadas, atrasoMedio };
  }, [empresas]);

  // Gargalo por departamento: atraso médio de contábil/fiscal/folha.
  const gargalo = useMemo(() => {
    const acc = { contabil: [0, 0], fiscal: [0, 0], folha: [0, 0] } as Record<
      string,
      [number, number]
    >;
    empresas.forEach(e => {
      (
        [
          ['contabil', e.contabil],
          ['fiscal', e.fiscal],
          ['folha', e.folha],
        ] as const
      ).forEach(([k, d]) => {
        if (d) {
          acc[k][0] += num(d.atraso);
          acc[k][1] += 1;
        }
      });
    });
    return (['contabil', 'fiscal', 'folha'] as const).map(k => ({
      depto: k,
      label: DEPTO_LABEL[k],
      media: acc[k][1] ? acc[k][0] / acc[k][1] : 0,
    }));
  }, [empresas]);
  const maxGargalo = Math.max(...gargalo.map(g => g.media), 1);

  // Distribuição de atraso em faixas.
  const faixas = useMemo(
    () =>
      FAIXAS.map(f => ({
        ...f,
        qtd: empresas.filter(e => {
          const a = num(e.atraso);
          return a >= f.min && a < f.max;
        }).length,
      })),
    [empresas],
  );
  const maxFaixa = Math.max(...faixas.map(f => f.qtd), 1);

  const lista = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return [...empresas]
      .filter(e => !filtro || e.status === filtro)
      .filter(e => !q || `${e.nome} ${e.codi} ${e.responsavel ?? ''}`.toLowerCase().includes(q))
      .sort((a, b) => num(b.atraso) - num(a.atraso));
  }, [empresas, filtro, busca]);

  if (!visaoCarteira) {
    return (
      <EmptyState
        title="Acesso restrito"
        message="O acompanhamento de fechamento é uma visão gerencial de toda a carteira, disponível apenas para perfis com acesso a todos os grupos de empresas."
        icon={Buildings}
      />
    );
  }

  if (isError) {
    return (
      <EmptyState
        title="Não foi possível carregar"
        message="Ocorreu um erro ao buscar o acompanhamento de fechamento. Atualize a página e tente novamente."
        icon={WarningCircle}
      />
    );
  }

  return (
    <Box>
      <Grid templateColumns={{ base: '1fr 1fr', md: 'repeat(4, 1fr)' }} gap={4} mb={5}>
        <KpiCard
          label="Empresas"
          value={empresas.length}
          icon={Buildings}
          iconColor={UI.brand}
          iconBg={UI.infoSoft}
          isLoading={isFetching}
          info={
            <InfoBloco
              oque="Total de empresas da carteira com estado de fechamento acompanhado."
              dados="extract_fechamento × tb_empresa (e.id), uma linha por empresa ativa"
              filtro={FILTRO.carteira}
            />
          }
        />
        <KpiCard
          label="No prazo"
          value={resumo.noPrazo}
          icon={CheckCircle}
          iconColor={UI.pos}
          iconBg={UI.posSoft}
          isLoading={isFetching}
          onClick={() => setFiltro('green')}
          info={
            <InfoBloco
              oque="Empresas com fechamento em dia em todas as áreas (atraso até 30 dias contra a data atual)."
              dados="extract_fechamento.contabil_fechado_ate / fiscal_fechado_ate / folha_encerrada_ate vs CURRENT_DATE"
              filtro={FILTRO.carteira}
            />
          }
        />
        <KpiCard
          label="Atrasadas"
          value={resumo.atrasadas}
          sub="+60 dias"
          icon={WarningCircle}
          iconColor={UI.neg}
          iconBg={UI.negSoft}
          isLoading={isFetching}
          onClick={() => setFiltro('red')}
          info={
            <InfoBloco
              oque="Empresas com atraso crítico de fechamento (mais de 60 dias em alguma área, ou área nunca fechada)."
              dados="CURRENT_DATE − extract_fechamento.*_fechado_ate > 60 (data NULL conta como atrasada)"
              filtro={FILTRO.carteira}
            />
          }
        />
        <KpiCard
          label="Atraso médio"
          value={`${resumo.atrasoMedio} dias`}
          icon={CalendarBlank}
          iconColor={UI.warn}
          iconBg={UI.warnSoft}
          isLoading={isFetching}
          info={
            <InfoBloco
              oque="Média, em dias, do maior atraso de fechamento por empresa na carteira."
              dados="média de max(CURRENT_DATE − extract_fechamento.*_fechado_ate) por empresa ativa"
              filtro={FILTRO.carteira}
            />
          }
        />
      </Grid>

      {/* Gargalo por setor + faixas de atraso */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5} mb={5}>
        <Glass title="Gargalo por departamento" sub="atraso médio (dias)">
          {gargalo.map(g => (
            <Flex key={g.depto} align="center" gap={3} mb={2.5}>
              <Text w="64px" fontSize="12px" fontWeight={700} color={UI.ink} flexShrink={0}>
                {g.label}
              </Text>
              <Box flex={1} h="9px" borderRadius="full" bg="rgba(15,23,42,0.06)" overflow="hidden">
                <Box
                  h="100%"
                  borderRadius="full"
                  bg={corAtraso(g.media)}
                  w={`${(g.media / maxGargalo) * 100}%`}
                />
              </Box>
              <Text
                w="60px"
                textAlign="right"
                fontSize="13px"
                fontWeight={800}
                color={UI.ink}
                flexShrink={0}
              >
                {Math.round(g.media)}d
              </Text>
            </Flex>
          ))}
        </Glass>

        <Glass title="Distribuição de atraso" sub={`${empresas.length} empresas`}>
          {faixas.map(f => (
            <Flex key={f.label} align="center" gap={3} mb={2.5}>
              <Text w="84px" fontSize="12px" fontWeight={700} color={UI.muted} flexShrink={0}>
                {f.label}
              </Text>
              <Box flex={1} h="9px" borderRadius="full" bg="rgba(15,23,42,0.06)" overflow="hidden">
                <Box h="100%" borderRadius="full" bg={f.color} w={`${(f.qtd / maxFaixa) * 100}%`} />
              </Box>
              <Text
                w="80px"
                textAlign="right"
                fontSize="12.5px"
                fontWeight={800}
                color={UI.ink}
                flexShrink={0}
              >
                {f.qtd} · {pct(empresas.length ? (f.qtd / empresas.length) * 100 : 0)}
              </Text>
            </Flex>
          ))}
        </Glass>
      </SimpleGrid>

      <Flex gap={3} align="center" wrap="wrap" mb={4}>
        <Select
          size="sm"
          w="180px"
          borderRadius="10px"
          bg="white"
          value={filtro}
          onChange={e => setFiltro(e.target.value)}
        >
          {FILTROS.map(f => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </Select>
        <InputGroup size="sm" w={{ base: '100%', md: '300px' }}>
          <InputLeftElement pointerEvents="none" color={UI.muted}>
            <MagnifyingGlass size={15} />
          </InputLeftElement>
          <Input
            placeholder="Razão social, código ou responsável…"
            value={busca}
            onChange={e => setBusca(e.target.value)}
            borderRadius="10px"
            bg="white"
          />
        </InputGroup>
        {busca && (
          <Text fontSize="12px" color={UI.muted}>
            {lista.length} de {empresas.length}
          </Text>
        )}
      </Flex>

      <Box
        borderRadius="18px"
        overflow="hidden"
        bg="rgba(255,255,255,0.7)"
        backdropFilter="blur(14px) saturate(160%)"
        border="1px solid rgba(255,255,255,0.85)"
        boxShadow="0 12px 34px -18px rgba(15,23,42,0.35)"
      >
        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead bg={UI.rowHover}>
              <Tr>
                <Th>ID</Th>
                <Th>Razão Social</Th>
                <Th>Regime</Th>
                <Th>Responsável</Th>
                <Th>Contábil</Th>
                <Th>Fiscal</Th>
                <Th>Folha</Th>
                <Th isNumeric>Atraso</Th>
                <Th>Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {isFetching && (
                <Tr>
                  <Td colSpan={9} textAlign="center" py={8}>
                    <Spinner />
                  </Td>
                </Tr>
              )}
              {!isFetching &&
                lista.map(e => (
                  <Tr
                    key={e.id}
                    cursor="pointer"
                    _hover={{ bg: UI.rowHover }}
                    onClick={() => onOpenEmpresa(e.id)}
                    title="Abrir o dashboard desta empresa"
                  >
                    <Td fontWeight={700} color={UI.brand}>
                      {e.codi}
                    </Td>
                    <Td maxW="240px" whiteSpace="normal" fontWeight={700}>
                      {e.nome}
                    </Td>
                    <Td>{e.regime}</Td>
                    <Td color={e.responsavel ? UI.ink : UI.muted}>{e.responsavel ?? '—'}</Td>
                    <Td>
                      <DeptoCell
                        depto={e.contabil}
                        onOpen={() => onOpenEmpresaScreen(e.id, DEPTO_SCREEN.contabil)}
                      />
                    </Td>
                    <Td>
                      <DeptoCell
                        depto={e.fiscal}
                        onOpen={() => onOpenEmpresaScreen(e.id, DEPTO_SCREEN.fiscal)}
                      />
                    </Td>
                    <Td>
                      <DeptoCell
                        depto={e.folha}
                        onOpen={() => onOpenEmpresaScreen(e.id, DEPTO_SCREEN.folha)}
                      />
                    </Td>
                    <Td isNumeric fontWeight={700} color={STATUS_COLOR[e.status]}>
                      {e.atraso == null ? '—' : `${e.atraso}d`}
                    </Td>
                    <Td>
                      <FechamentoBadge status={e.status} />
                    </Td>
                  </Tr>
                ))}
              {!isFetching && lista.length === 0 && (
                <Tr>
                  <Td colSpan={9} textAlign="center" py={8} color={UI.muted}>
                    Nenhuma empresa para o filtro selecionado.
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}
